import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ClockIcon } from "@heroicons/react/24/outline";

import { IProductionDashboardBayVehicle } from "../../models/ProductionDashboard";
import classNames from "../../utils/classNames";
import { cycleTimeBand } from "../../utils/cycleTimeBand";

interface AssemblyTimerProps {
  vehicle: IProductionDashboardBayVehicle;
  now: DateTime;
  size?: "sm" | "md";
}

const formatDuration = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
};

// Live timer for the assembly a vehicle is currently on. Elapsed time is the accumulated
// worked time from completed (paused) session segments plus, if a session is currently
// running, the time since it started. It flags amber once it passes the assembly's warning
// time and red once it passes the assembly's target - both set in the Part Assembly Editor.
export default function AssemblyTimer({ vehicle, now, size = "md" }: AssemblyTimerProps) {
  const { t } = useTranslation("production");
  const {
    currentAssemblyElapsedSeconds,
    currentAssemblyStartedOn,
    currentAssemblyAverageMinutes,
    currentAssemblyWarningMinutes,
  } = vehicle;

  const runningSeconds = currentAssemblyStartedOn
    ? Math.max(
        0,
        now.diff(DateTime.fromISO(currentAssemblyStartedOn, { zone: "utc" }), "seconds").seconds
      )
    : 0;

  const elapsedSeconds = (currentAssemblyElapsedSeconds ?? 0) + runningSeconds;

  // Nothing has been timed for this assembly yet — no session recorded.
  if (elapsedSeconds <= 0) return null;

  const isRunning = !!currentAssemblyStartedOn;
  const band = cycleTimeBand(
    elapsedSeconds,
    currentAssemblyWarningMinutes,
    currentAssemblyAverageMinutes
  );
  const isOver = band === "over";
  const isWarning = band === "warning";

  // Name the breached threshold rather than just the target, so a team leader can tell an amber
  // "past the warning time" from a red "past the target" without opening the assembly editor.
  const title = isOver
    ? t("Target {{minutes}}m — running over", { minutes: currentAssemblyAverageMinutes })
    : isWarning
      ? t("Over the {{minutes}}m warning time", { minutes: currentAssemblyWarningMinutes })
      : currentAssemblyAverageMinutes
        ? t("Target {{minutes}}m", { minutes: currentAssemblyAverageMinutes })
        : currentAssemblyWarningMinutes
          ? t("Warning at {{minutes}}m", { minutes: currentAssemblyWarningMinutes })
          : t("No target set for this assembly");

  return (
    <div
      className={classNames(
        "inline-flex items-center gap-1 rounded font-medium tabular-nums",
        size === "sm" ? "text-[10px] px-1 py-0.5" : "text-[11px] px-1.5 py-0.5",
        isOver
          ? "bg-red-50 text-red-700 border border-red-200"
          : isWarning
            ? "bg-amber-50 text-amber-700 border border-amber-200"
            : isRunning
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-gray-50 text-gray-600 border border-gray-200"
      )}
      title={title}
    >
      <ClockIcon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
      <span>{formatDuration(elapsedSeconds)}</span>
      {currentAssemblyAverageMinutes ? (
        <span
          className={isOver ? "text-red-500" : isWarning ? "text-amber-500" : "text-gray-400"}
        >
          / {currentAssemblyAverageMinutes}m
        </span>
      ) : null}
    </div>
  );
}
